import { Injectable } from '@angular/core';

import {
  asyncScheduler,
  BehaviorSubject,
  combineLatest,
  Observable,
} from 'rxjs';
import {
  map,
  tap,
  take,
  finalize,
  startWith,
  filter,
  debounceTime,
} from 'rxjs/operators';
import { normalizeItems } from './services/profile-collection-helpers';

import {
  ProfileCollectionItem,
  CollectionFilter,
  ProfileCollectionFacets,
  CollectionItem,
  CollectionItemType,
  CollectionFacet,
} from '@app/profile/profile-api.model';
import { ProfileCollectionServiceV1 } from './services/profile-collection.service';
import { ActivatedRoute, Router } from '@angular/router';
import { InputsService } from '@app/inputs/services/inputs.service';
import { UserOutcomeService } from '@app/outcomes/services/user-outcome.service';
import { ModalService } from '@app/shared/services/modal.service';
import { NotifierService } from '@app/shared/services/notifier.service';

import { Profile } from '@app/profile/profile.model';
import { TagsApi } from '@app/tags/tag-api.model';

import { initFilter } from './services/profile-collection-helpers';
import { TrackerService } from '@app/shared/services/tracker.service';
import { DatePipe, TitleCasePipe } from '@angular/common';

import { cloneDeep as _cloneDeep } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';
import { SimpleModalComponent } from '@app/shared/components/modal/simple-modal/simple-modal.component';
import { ProfileCollectionBulkUpdateModalV1 } from '@app/profile/components/profile-collection-v1/bulk-update/update-modal/profile-collection-bulk-update-modal.component';
import {
  AnyLearningResource,
  LearningResourceViewModel,
} from '@app/inputs/models/learning-resource.view-model';
import { ProfileOverviewActionsService } from '@app/profile/components/profile-overview/profile-overview-actions/profile-overview-actions.service';
import { InputType, OutcomeType } from '@app/shared/models/core-api.model';
import { InputModalDispatcherService } from '@app/user-content/user-input/services/input-modal-dispatcher.service';
import { UserOutcomeModalService } from '@app/outcomes/services/user-outcome-modal.service';
import { PopoverService } from '@dg/shared-services';
import { AnyInputApiEntity } from '@app/user-content/user-input/user-input.model';
import { WindowLayoutService } from '@app/shared/services/window-layout/window-layout.service';
import { ProfileService } from '@app/profile/services/profile.service';
import { getEmptyTileCount } from '@app/profile/components/profile-overview-v2/profile-overview.utils';
import { UserOutcomeActionsService } from '@app/profile/components/user-outcome-tile-v2/user-outcome-actions/user-outcome-actions.service';

// map from the ids supplied by the BE to more appropriate ids
const FACET_ID_MAP = {
  Category: 'tags',
  Provider: 'providers',
  ExperienceType: 'experienceType',
  PathwayStatus: 'pathwayStatus',
  TargetStatus: 'targetStatus',
};

const ITEM_TYPE_MAP = {
  learning: [
    'article',
    'book',
    'video',
    'course',
    'episode',
    'event',
    'assessment',
    'task',
    'post',
  ],
  achievements: ['degree', 'certificate', 'badge', 'accomplishment', 'award'],
  experiences: ['position'],
};

export interface SortKey {
  label: string;
  key: string;
  direction: string;
}

export interface AddKey {
  label: string;
  key: string;
  itemType: string;
}

interface BulkUpdateVM {
  enabled: boolean;
  deleteModalHeaderText: string;
  deleteModalBodyText: string;
  clearSelectionText: string;
  selectedItemsText: string;
  selectedItems?: ProfileCollectionItem<any>[];
}

export interface CollectionViewModel {
  items: ProfileCollectionItem<any>[];
  processedFacets: ProfileCollectionFacets;
  displayFacets?: CollectionFacet[];
  hasMoreItems: boolean;
  filter: CollectionFilter;
  filterToFocus?: string;
  fromFilterUpdate?: boolean;
  experienceTypes?: CollectionItemType[];
  inputTypes?: CollectionItemType[];
  outcomeTypes?: CollectionItemType[];
  bulkUpdateVM?: BulkUpdateVM;
  sorting: SortKey;
  addKeys: AddKey[];
  isMobile: boolean;
  placeholderTiles: number[];
  tileRowLimit: number;
  indexesOfCardsToExpand: number[];
}

export interface StatusVM {
  loading: boolean;
  appending: boolean;
  filterUpdating: boolean;
}

@Injectable()
export class ProfileCollectionFacadeV1 {
  /**
   * Scheduler used for `debounceTime()` for the profile collection
   * This allows tests to override with a virtual scheduler
   */
  public static debounceScheduler = asyncScheduler;

  private _vm$: BehaviorSubject<CollectionViewModel>;
  private _status$: BehaviorSubject<StatusVM>;
  private _hasError$: BehaviorSubject<boolean>;

  private _reload$ = new BehaviorSubject<boolean>(true);
  private _enabled = true;

  private i18n = this.translateService.instant([
    'Core_Newest',
    'Core_Oldest',
    'Core_Atoz',
    'Core_Ztoa',
  ]);

  constructor(
    private _api: ProfileCollectionServiceV1,
    private route: ActivatedRoute,
    private inputsService: InputsService,
    private userOutcomeService: UserOutcomeService,
    private trackerService: TrackerService,
    private translateService: TranslateService,
    private datePipe: DatePipe,
    private router: Router,
    private modalService: ModalService,
    private notifierService: NotifierService,
    private profileOverviewActionsService: ProfileOverviewActionsService,
    private genericInputModalService: InputModalDispatcherService,
    private userOutcomeModalService: UserOutcomeModalService,
    private titleCasePipe: TitleCasePipe,
    private popover: PopoverService,
    private windowLayoutService: WindowLayoutService,
    private profileService: ProfileService,
    private userOutcomeActionsService: UserOutcomeActionsService
  ) {
    this._vm$ = new BehaviorSubject<CollectionViewModel>({
      items: [],
      processedFacets: { itemTypes: [], facets: [] },
      hasMoreItems: false,
      filter: {},
      displayFacets: [],
      bulkUpdateVM: {
        enabled: false,
        selectedItems: [],
        deleteModalHeaderText: '',
        deleteModalBodyText: '',
        clearSelectionText: '',
        selectedItemsText: '',
      },
      sorting: this.sortKeys[0],
      addKeys: [],
      isMobile: this.windowLayoutService.isMobile,
      placeholderTiles: [],
      tileRowLimit: 4,
      indexesOfCardsToExpand: [],
    });

    this.route.queryParams
      .pipe(
        map((params) => {
          this.updateVM({ filter: initFilter(params) });
          this.setAddKeys();
        })
      )
      .subscribe();

    this._status$ = new BehaviorSubject<{
      loading: boolean;
      appending: boolean;
      filterUpdating: boolean;
    }>({ loading: true, appending: false, filterUpdating: false });

    this._hasError$ = new BehaviorSubject<boolean>(false);

    // The triggers below are a basic WIP so that filtering on enabled could easily be added.
    // We've added https://degreedjira.atlassian.net/browse/PD-93319 for the 2024.0.1.0 sprint to start addressing this facade.
    const dataTriggers$ = combineLatest([
      this.userOutcomeService.userOutcomeModify.pipe(startWith({})),
      this.inputsService.inputModify.pipe(startWith({})),
    ]).pipe(
      filter(() => this.enabled),
      debounceTime(20, ProfileCollectionFacadeV1.debounceScheduler)
    );

    const selectionTriggers$ = combineLatest([
      this.profileOverviewActionsService.inputSelectedSubject$.pipe(
        startWith(null)
      ),
      this.profileOverviewActionsService.outcomeSelectedSubject$.pipe(
        startWith(null)
      ),
    ]).pipe(filter((items: any[]) => this.enabled && items.some((i) => !!i)));

    this.updateDataWhen(dataTriggers$);
    this.updateSelectionsWhen(selectionTriggers$);

    this.userOutcomeActionsService.itemSelected
      .pipe(filter(() => this.enabled))
      .subscribe((item) => {
        this.toggleItemSelection(item);
      });

    this.windowLayoutService.windowResize$
      .pipe(filter(() => this.enabled))
      .subscribe(() => {
        this.updateVM({ isMobile: this.windowLayoutService.isMobile });
      });
  }

  /**
   * Special property to either force reload or IGNORE other triggers
   * Use to temporarily disable the facade when not needed
   */
  public get enabled(): boolean {
    return this._enabled;
  }

  public set enabled(value: boolean) {
    if (value === this._enabled) return;
    this._enabled = value;

    // Force trigger$ emission to refresh the profile VM
    if (!!value) this._reload$.next(true);
  }

  public get vm$(): Observable<CollectionViewModel> {
    return this._vm$.asObservable();
  }

  public get status$(): Observable<StatusVM> {
    return this._status$.asObservable();
  }

  public get hasError$(): Observable<boolean> {
    return this._hasError$.asObservable();
  }

  public get pathwaysOrPlansDisplayed(): boolean {
    return ['pathways', 'plans'].includes(
      this._vm$.value.filter.currentItemClassifier
    );
  }

  public get owner(): Profile {
    return this.profileService.profileInput?.profile;
  }

  public get indexesOfCardsToExpand(): number[] {
    return this._vm$.value.indexesOfCardsToExpand;
  }

  public set indexesOfCardsToExpand(value: number[]) {
    if (value === this._vm$.value.indexesOfCardsToExpand) return;
    this._vm$.value.indexesOfCardsToExpand = value;
  }

  // @todo make this and onFiltersChanged more generic so that we don't need to update everytime a new filter is added
  public get hasFilterValues() {
    const {
      tags,
      type,
      term,
      providers,
      startDate,
      endDate,
      pathwayStatus,
      targetStatus,
      experienceType,
    } = this._vm$.value.filter;
    return (
      !!tags.length ||
      !!type.length ||
      term.length ||
      providers.length ||
      startDate !== null ||
      endDate !== null ||
      !!pathwayStatus.length ||
      !!targetStatus.length ||
      !!experienceType.length
    );
  }

  public get hasSearchTerm(): boolean {
    return !!this._vm$.value.filter.term;
  }

  public get sortKeys(): SortKey[] {
    return [
      {
        label: this.i18n.Core_Newest,
        key: 'date',
        direction: 'desc',
      },
      {
        label: this.i18n.Core_Oldest,
        key: 'date',
        direction: 'asc',
      },
      {
        label: this.i18n.Core_Atoz,
        key: 'title',
        direction: 'asc',
      },
      {
        label: this.i18n.Core_Ztoa,
        key: 'title',
        direction: 'desc',
      },
    ];
  }

  private updateDataWhen(triggers$: Observable<unknown>): void {
    // !! do not cache the `unsubscribe()` as this is a permanent connection
    triggers$.subscribe(() => this.getCollectionItems(false));
  }

  private updateSelectionsWhen(triggers$: Observable<unknown>): void {
    // !! do not cache the `unsubscribe()` as this is a permanent connection
    triggers$.subscribe((models: LearningResourceViewModel[]) => {
      for (const model of models) {
        if (!!model) {
          const item = this.getItemForModel(model);
          this.toggleItemSelection(item);
        }
      }
    });
  }

  public setSortKey(sortKey: SortKey): void {
    this.updateVM({ sorting: sortKey });
    this.getCollectionItems(false);
  }

  public setAddKeys(): void {
    const { currentItemClassifier } = this._vm$.value.filter;
    if (['pathways', 'plans'].includes(currentItemClassifier)) {
      this.updateVM({ addKeys: [] });
      return;
    }
    const excluded = ['task', 'post'];

    const itemTypes = ITEM_TYPE_MAP[currentItemClassifier].filter(
      (t) => !excluded.includes(t)
    );
    if (!itemTypes) {
      // Add button is not shown for plans and pathways
      this.updateVM({ addKeys: [] });
      return;
    }
    const itemTypeNameMap = {
      learning: 'input',
      experiences: 'input',
      achievements: 'outcome',
    };
    const addKeys = itemTypes.map((i) => {
      const key = this.titleCasePipe.transform(i);
      return {
        key,
        label: this.translateService.instant(`Core_${key}`),
        itemType: itemTypeNameMap[currentItemClassifier],
      };
    });
    this.updateVM({ addKeys });
  }

  public getItemForModel(
    model: LearningResourceViewModel
  ): ProfileCollectionItem<any> {
    const contentModel = model.model as AnyLearningResource;
    return this._vm$.value.items.find(
      (item) => item.itemId === contentModel.inputId
    );
  }

  public showBulkDeleteModal(): void {
    this.modalService
      .show<any>(SimpleModalComponent, {
        inputs: {
          canCancel: true,
          bodyClasses: 'center-text',
          headerText: this._vm$.value.bulkUpdateVM.deleteModalHeaderText,
          bodyText: this._vm$.value.bulkUpdateVM.deleteModalBodyText,
          submitButtonText: this.translateService.instant('Core_Delete'),
        },
      })
      .subscribe(() => {
        const numItems = this._vm$.value.bulkUpdateVM.selectedItems.length;
        this.deleteSelectedCollectionItems().subscribe(() => {
          this.resetBulkUpdate();
          this.notifierService.showSuccess(
            this.translateService.instant(
              'ProfileCtrl_DeleteCollectionMultipleItemsNotifierFormat',
              {
                selectedLength: numItems,
              }
            )
          );
        });
      });
  }

  public showBulkUpdateModal(): void {
    const inputs = { tags: [] };
    this.modalService
      .show(ProfileCollectionBulkUpdateModalV1, {
        inputs,
      })
      .subscribe((updatedTags: TagsApi.Tag[]) => {
        const numItems =
          this._vm$.value.bulkUpdateVM.selectedItems.length.toString();
        this.updateSelectedCollectionItems(updatedTags).subscribe(() => {
          this.resetBulkUpdate();
          this.notifierService.showSuccess(
            this.translateService.instant(
              'ProfileCtrl_EditCollectionMultipleItemsNotifierFormat',
              {
                selectedLength: numItems,
              }
            )
          );
        });
      });
  }

  public toggleBulkEditMode(): void {
    this._vm$.value.bulkUpdateVM.enabled
      ? this.resetBulkUpdate()
      : this.setBulkEditMode();
  }

  public setBulkEditMode(
    selectedItems?: ProfileCollectionItem<any>[],
    enabled: boolean = true
  ): void {
    const items = selectedItems || this._vm$.value.bulkUpdateVM.selectedItems;
    const count = items.length;

    this.updateVM({
      bulkUpdateVM: {
        enabled,
        selectedItems: items,
        selectedItemsText: this.translateService.instant(
          'Core_ItemsSelectedFormat',
          {
            count,
          }
        ),
        clearSelectionText:
          count === 1
            ? this.translateService.instant('Core_ClearSelectionSingle')
            : this.translateService.instant('Core_ClearSelectionPluralFormat', {
                count,
              }),
        deleteModalHeaderText:
          count === 1
            ? this.translateService.instant('ProfileCtrl_BulkDeleteSingle')
            : this.translateService.instant('ProfileCtrl_BulkDeleteFormat', {
                selectedLength: count,
              }),
        deleteModalBodyText:
          count === 1
            ? this.translateService.instant(
                'ProfileCtrl_DeleteCollectionGenericItemBodySingle'
              )
            : this.translateService.instant(
                'ProfileCtrl_DeleteCollectionGenericItemBody'
              ),
      },
    });
  }

  public toggleItemSelection(collectionItem: ProfileCollectionItem<any>): void {
    const selected = !this._vm$.value.bulkUpdateVM.selectedItems.find(
      (item) => item.userItemId === collectionItem.userItemId
    );
    const selectedItems = selected
      ? [...this._vm$.value.bulkUpdateVM.selectedItems, collectionItem]
      : this._vm$.value.bulkUpdateVM.selectedItems.filter(
          (item) => item.itemId !== collectionItem.itemId
        );
    this.setBulkEditMode(selectedItems, !!selectedItems.length);
  }

  public deleteSelectedCollectionItems(): Observable<any> {
    return this._api
      .deleteCollectionItems(
        normalizeItems(this._vm$.value.bulkUpdateVM.selectedItems)
      )
      .pipe(
        tap(() => {
          this.resetBulkUpdate();
          this.getCollectionItems(false);
        })
      );
  }

  public resetBulkUpdate(): void {
    this.updateVM({
      bulkUpdateVM: {
        enabled: false,
        selectedItems: [],
        selectedItemsText: '',
        clearSelectionText: '',
        deleteModalHeaderText: '',
        deleteModalBodyText: '',
      },
    });
  }

  public searchItems(searchTerm: string): void {
    this.updateFilter('term', searchTerm);
    this.onFiltersChanged();

    this.updateStatus({ filterUpdating: true });
    this.getCollectionItems(false, true);
    this.trackSearch();
  }

  public searchFacets(facetId: string, searchTerm: string) {
    const filterValueMap = {
      tags: 'id',
      providers: 'name',
      experienceType: 'id',
    };

    const { processedFacets, displayFacets } = this._vm$.value;
    const baseFacet = processedFacets.facets.find(
      (facet) => facet.id.toLowerCase() === facetId.toLowerCase()
    );

    const displayFacet = displayFacets.find(
      (f) => f.id.toLowerCase() === facetId.toLowerCase()
    );

    if (!searchTerm) {
      displayFacet.values = _cloneDeep(baseFacet.values);
    } else {
      displayFacet.values = baseFacet.values.filter((v) =>
        v[filterValueMap[facetId]]
          .toLowerCase()
          .startsWith(searchTerm.toLowerCase())
      );
    }
    this.updateVM({ displayFacets });
  }

  public updateDateFilterValues(startDate: string, endDate: string) {
    const newFilter = {
      ...this._vm$.value.filter,
      startDate,
      endDate,
      filterToFocus: 'date',
    };
    this.updateVM({ filter: newFilter });
    this.onFiltersChanged();
    this.updateStatus({ filterUpdating: true });
    this.getCollectionItems(false, true);

    this.trackFiltering('date');
  }

  public updateFilterValue(
    filterType: string,
    filterItem: CollectionItemType
  ): void {
    const filterValueMap = {
      type: 'name',
    };

    let filterValues = this._vm$.value.filter[filterType];
    const value = filterItem[filterValueMap[filterType] || 'id'];

    if (filterValues.includes(value)) {
      filterValues = filterValues.filter((t) => t !== value);
    } else {
      filterValues.push(value);
    }

    this.updateFilter(filterType, filterValues);

    this.updateStatus({ filterUpdating: true });
    this.getCollectionItems(false, true);

    this.trackFiltering(filterType);
  }

  public updateFilterValues(
    filterType: string,
    filterValues: string[]
  ): void {
    this.updateFilter(filterType, filterValues);

    this.updateStatus({ filterUpdating: true });
    this.getCollectionItems(false, true);

    this.trackFiltering(filterType);
  }

  public resetFilterValue(filterType: string): void {
    this.updateFilter(filterType, []);
    this.getCollectionItems(false, true);
  }

  // @todo make this and onFilterChanges and on/filtersChanged more generic so that we don't need to update everytime a new filter is added
  public resetFilters(classifier?): void {
    const filter = {
      type: [],
      tags: [],
      term: '',
      providers: [],
      experienceType: [],
      startDate: null,
      endDate: null,
      currentItemClassifier:
        classifier || this._vm$.value.filter.currentItemClassifier,
      pathwayStatus: [],
      targetStatus: [],
    };
    this.updateVM({ filter });
    this.onFiltersChanged();
    this.getCollectionItems(false);
  }

  public getCollectionItems(append: boolean, fromFilterUpdate?: boolean): void {
    const classifier = this._vm$.value.filter.currentItemClassifier;
    switch (classifier) {
      case 'learning':
      case 'experiences':
      case 'achievements':
        this.getInputsAndOutcomes(append, fromFilterUpdate);
        break;
      case 'pathways':
      case 'plans':
        this.getPlansAndPathways(classifier, fromFilterUpdate);
    }
    this.indexesOfCardsToExpand = [];
  }

  public updateSelectedCollectionItems(
    updatedTags: TagsApi.Tag[]
  ): Observable<void> {
    const items = normalizeItems(this._vm$.value.bulkUpdateVM.selectedItems);
    return this._api.updateCollectionItems(items, updatedTags);
  }

  public setCurrentItemClassifier(classifier: string): void {
    this.resetBulkUpdate();
    this.resetFilters(classifier);
    this.setAddKeys();
    this.trackFiltering('filter tab');
  }

  public showAddModal(addKey: AddKey, buttonRef: HTMLElement): void {
    const showModal: Observable<AnyInputApiEntity | void> =
      addKey.itemType === 'input'
        ? this.genericInputModalService.complete({
            inputType: addKey.key as InputType,
            sourceTarget: buttonRef,
          })
        : this.userOutcomeModalService.add({
            outcomeType: addKey.key as OutcomeType,
            sourceTarget: buttonRef,
          });

    showModal
      .pipe(
        take(1),
        finalize(() => {
          this.popover.close();
        })
      )
      .subscribe(() => {
        if (addKey.itemType === 'outcome') {
          this.userOutcomeService.notifyOutcomeModified();
        }
      });
  }

  private getInputsAndOutcomes = (
    append: boolean,
    fromFilterUpdate?: boolean
  ): void => {
    this.updateStatus({ loading: true, appending: append });

    const filter = { ...this._vm$.value.filter };
    if (!filter.type?.length) {
      filter.type = ITEM_TYPE_MAP[this._vm$.value.filter.currentItemClassifier];
    }
    // don't reset the facets when filters are applied or the facets are lost for the filtered out items
    this._api
      .getCollectionItems(filter, this.owner.id, append)
      .pipe(take(1))
      .subscribe(
        (collectionItems: CollectionItem<any>) => {
          this._hasError$.next(false);
          const { items, processedFacets, hasMoreItems } = collectionItems;

          let allItems = items;
          if (append) {
            allItems = [...this._vm$.value.items, ...items];
          }
          this.updateStatus();
          this.updateVM({
            hasMoreItems,
            processedFacets,
            items: allItems,
            fromFilterUpdate,
            ...this.getItemTypes(processedFacets.itemTypes),
            displayFacets: this.getDisplayFacets(processedFacets.facets),
            placeholderTiles: getEmptyTileCount(
              allItems.length,
              this._vm$.value.tileRowLimit
            ),
          });
        },
        (error) => {
          console.error(error);
          this._hasError$.next(true);
          this.updateStatus();
        }
      );
  };

  private getPlansAndPathways = (
    contentType: string,
    fromFilterUpdate?: boolean
  ): void => {
    // no pagination for plans or pathways yet
    this.updateStatus({ loading: true, appending: false });

    const api =
      contentType === 'plans'
        ? this._api.getPlansAsCollectionItems.bind(this._api)
        : this._api.getPathwaysAsCollectionItems.bind(this._api);

    api(this.owner.id, this._vm$.value.filter, this._vm$.value.sorting)
      .pipe(take(1))
      .subscribe(
        ({ items, processedFacets }) => {
          this._hasError$.next(false);
          this.updateStatus();
          this.updateVM({
            items,
            processedFacets,
            displayFacets: this.getDisplayFacets(processedFacets.facets),
            hasMoreItems: false,
            fromFilterUpdate,
            placeholderTiles: getEmptyTileCount(
              items.length,
              this._vm$.value.tileRowLimit
            ),
          });
        },
        (error) => {
          console.error(error);
          this._hasError$.next(true);
          this.updateStatus();
        }
      );
  };

  private getDisplayFacets(allFacets) {
    return _cloneDeep(
      allFacets.reduce((allFacets, facet) => {
        facet.id = FACET_ID_MAP[facet.id] || facet.id;
        allFacets.push(facet);
        return allFacets;
      }, [])
    );
  }

  private getItemTypes(itemTypes): {
    inputTypes: CollectionItemType[];
    outcomeTypes: CollectionItemType[];
    experienceTypes: CollectionItemType[];
  } {
    const inputTypes = itemTypes.filter((t) => {
      return t.type === 'Content' && t.name !== 'Position';
    });
    const outcomeTypes = itemTypes.filter((t) => {
      return t.type === 'Achievement' && t.name !== 'Skill';
    });
    const experienceTypes = itemTypes.filter((t) => {
      return t.name === 'Position';
    });
    return { inputTypes, outcomeTypes, experienceTypes };
  }

  private updateVM(args) {
    this._vm$.next({
      ...this._vm$.value,
      ...args,
    });
  }

  private updateFilter(
    filterType: string,
    filterValues: string | string[]
  ): void {
    const newFilter = {
      ...this._vm$.value.filter,
      [filterType]: filterValues,
      filterToFocus: filterType,
    };
    this.updateVM({ filter: newFilter });
    this.onFiltersChanged();
  }

  private updateStatus(args?): void {
    if (args) {
      this._status$.next({ ...this._status$.value, ...args });
    } else {
      this._status$.next({
        loading: false,
        appending: false,
        filterUpdating: false,
      });
    }
  }

  private trackFiltering(filterType: string): void {
    const idMap = {
      tags: 'Skills',
      providers: 'Provider',
      experienceType: 'ExperienceType',
      type: 'Type',
      date: 'Date',
    };
    let filterLabel;
    let startDate = null;
    let endDate = null;
    const formatDate = (date) =>
      !!date ? this.datePipe.transform(date, 'd MMM y') : undefined;

    switch (filterType) {
      case 'tags':
        // get the last added tag
        filterLabel = this._vm$.value.filter.tags.slice(-1)[0];
        break;
      case 'providers':
        // get the last added provider
        filterLabel = this._vm$.value.filter.providers.slice(-1)[0];
        break;
      case 'experienceType':
        // get the last added provider
        filterLabel = this._vm$.value.filter.experienceType.slice(-1)[0];
        break;
      case 'date':
        filterLabel = 'dates';
        startDate = formatDate(this._vm$.value.filter.startDate);
        endDate = formatDate(this._vm$.value.filter.endDate);
        break;
      case 'type':
        filterLabel = this._vm$.value.filter.type.join(',');
        break;
      default:
        filterLabel = filterType;
        break;
    }

    this.trackerService.trackEventData({
      action: 'Profile Collection Filtered',
      category: 'Collection',
      label: filterLabel,
      properties: {
        filterType: idMap[filterType],
        StartDate: startDate,
        EndDate: endDate,
        FilterTabClicked: this._vm$.value.filter.currentItemClassifier,
      },
    });
  }

  private trackSearch(): void {
    const { term } = this._vm$.value.filter;
    const searchTerm = term ? term.toLowerCase() : '';
    this.trackerService.trackEventData({
      action: 'Profile Collection Searched',
      category: 'Collection',
      label: 'search',
      properties: { Keywords: searchTerm },
    });
  }

  /**
   * When the filter has changed, create the new collection page url and navigate to the new URL to set it in the
   * history without reloading the page. Return the promise from navigateByUrl so that when it is complete we can update
   * the collection using the URL params
   *
   * @todo make this and resetFilters more generic so that we don't need to update everytime a new filter is added
   */
  private onFiltersChanged(): Promise<boolean> {
    const {
      providers,
      tags,
      term,
      type,
      startDate,
      endDate,
      currentItemClassifier,
      pathwayStatus,
      targetStatus,
      experienceType,
    } = this._vm$.value.filter;
    const params = {
      provider: providers,
      tags,
      term: !!term ? term : undefined,
      type: type,
      startDate,
      endDate,
      classifier: currentItemClassifier,
      pathwayStatus,
      targetStatus,
      experienceType,
    };

    const url = this.router
      .createUrlTree([], {
        relativeTo: this.route,
        queryParams: params,
      })
      .toString();
    return this.router.navigateByUrl(url);
  }
}
